import { PersonDto } from "@app/api2/api";
import { useFormikContext } from "formik";
import React from "react";
import { Button, Header, Message, Segment } from "@/components/bp/bp-ui";
import { useEnrollmentFormContext } from "../EnrollmentFormContext";
import { Repeater, RepeaterProps } from "./Repeater";
import { BPButton } from "@/components/bp/bpbutton";

type EnrollmentFormSectionProps = {
    sectionKey: string;
    header?: string;
    info?: string;
    children?: React.ReactNode | ((props: {
        editMode: boolean, shouldShowEditButton: boolean, isEditButtonEnabled: boolean,
        invokeEdit?: () => void
    }) => React.ReactNode);
    basic?: boolean;
};

export const EnrollmentFormSection = ({ sectionKey: key, header, info, children, basic, }: EnrollmentFormSectionProps) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const formik = useFormikContext<PersonDto>();
    const enrollmentContext = useEnrollmentFormContext();

    const shouldShowSaveProgressButton = false;//!formik.values.isEnrollmentCompleted;
    const isSaveProgressButtonEnabled = !isSubmitting;

    const meEditing = enrollmentContext.editing && enrollmentContext.editingKey == key;
    const otherEditing = enrollmentContext.editing && !meEditing;
    const nobodyEditing = !enrollmentContext.editing;



    const isSaveCancelButtonEnabled = !isSubmitting && meEditing;
    const shouldShowSaveCancelButton = meEditing && formik.values.isEnrollmentCompleted;


    const isEditButtonEnabled = !isSubmitting && nobodyEditing;
    const shouldShowEditButton = formik.values.isEnrollmentCompleted && (!meEditing || nobodyEditing);

    const editMode = basic || !formik.values.isEnrollmentCompleted || meEditing;

    return (
        <Segment>
            {header ? <Header as="h3">{header}</Header> : null}
            {info ? <Message
                size="small"
                content={info} /> : null}
            {children && typeof children === 'function' ? children({ editMode, shouldShowEditButton, isEditButtonEnabled, invokeEdit: () => enrollmentContext.setEditingKey(key) }) : null}
            {children && typeof children !== 'function' ? children : null}
            {!basic && (<>
                {/* {children && (disabled && typeof children === 'function' ? children({ editMode }) : children)} */}
                {shouldShowEditButton && <BPButton type="button" disabled={!isEditButtonEnabled} onClick={() => {
                    return enrollmentContext.setEditingKey(key);
                }}>Edit</BPButton>}
                {(shouldShowSaveCancelButton || shouldShowSaveProgressButton) && <BPButton loading={isSubmitting} disabled={!isSaveCancelButtonEnabled && !isSaveProgressButtonEnabled} type="button" onClick={async () => {
                    setIsSubmitting(true);
                    const result = await formik.submitForm();
                    setIsSubmitting(false);
                    if (result == undefined) return;
                    enrollmentContext.setEditingKey();
                }}>{shouldShowSaveProgressButton ? 'Save Progress' : 'Save'}</BPButton>}
                {shouldShowSaveCancelButton && <BPButton disabled={!isSaveCancelButtonEnabled} type="button" onClick={async () => {
                    formik.resetForm();
                    enrollmentContext.setEditingKey();
                }}>Cancel</BPButton>}
            </>)}
        </Segment>
    );
};


type EnrollmentFormSectionWithRepeaterProps<TInstance extends any, T extends any, K extends keyof TInstance> = Omit<EnrollmentFormSectionProps, 'children'> & Omit<RepeaterProps<TInstance, T, K>, 'children'> & {
    children?: React.ReactNode | ((props: { item, idx: number, editMode: boolean }) => React.ReactNode);
};
// type EnrollmentFormSectionWithRepeaterProps<TInstance extends any, T extends any, K extends keyof TInstance> = {
//     sectionProps: Omit<EnrollmentFormSectionProps, 'children'>;
//     repeaterProps: Omit<RepeaterProps<TInstance, T, K>, 'children'>;
//     children?: React.ReactNode | ((props: { item, idx: number, editMode: boolean }) => React.ReactNode);
// };

export function EnrollmentFormSectionWithRepeater<TInstance extends any, T extends {}, K extends keyof TInstance>({ addButtonLabel, instanceType, maxItems, name, newItemFactory, children, sectionKey, header, info, basic }: EnrollmentFormSectionWithRepeaterProps<TInstance, T, K>) {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const formik = useFormikContext<PersonDto>();
    const enrollmentContext = useEnrollmentFormContext();
    const { values } = formik;

    return (
        <EnrollmentFormSection sectionKey={sectionKey} header={header} info={info} basic={basic}>
            {({ editMode, shouldShowEditButton, isEditButtonEnabled, invokeEdit }) => (
                <Repeater
                    maxItems={maxItems}
                    disabled={!editMode && (!shouldShowEditButton || !isEditButtonEnabled)}
                    onChange={() => invokeEdit()}
                    instanceType={instanceType}
                    name={name}
                    newItemFactory={newItemFactory}
                    addButtonLabel={addButtonLabel}>
                    {({ item, idx }) => (typeof children === 'function' ? children({ item, idx, editMode }) : children)}
                </Repeater>
            )}
        </EnrollmentFormSection>
    );
}
